/**
 * Avisos efimeros.
 *
 * En la caja no se puede interrumpir con un dialogo que exige un clic: el
 * cajero tiene las manos ocupadas y la fila avanzando. Estos avisos aparecen,
 * se leen de reojo y se van solos.
 *
 * Los errores duran mas que las confirmaciones, porque hay que hacer algo con
 * ellos.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cx } from './ui.js';

type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastValue {
  show: (message: string, tone?: ToastTone) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

const DURATION: Record<ToastTone, number> = {
  success: 2600,
  info: 3200,
  error: 6000,
};

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = nextId.current++;
      // Tope de tres: una pila mas alta tapa la pantalla justo cuando algo
      // esta saliendo mal y hay que ver que pasa.
      setToasts((current) => [...current.slice(-2), { id, tone, message }]);
      window.setTimeout(() => dismiss(id), DURATION[tone]);
    },
    [dismiss],
  );

  const value = useMemo<ToastValue>(
    () => ({
      show,
      success: (message) => show(message, 'success'),
      error: (message) => show(message, 'error'),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/*
        DONDE APARECEN, Y POR QUE ARRIBA EN EL TELEFONO.

        Antes bajaban a `bottom-24`: 96 px medidos a mano para librar la barra de
        navegacion. Ese numero no puede estar bien. En la caja, debajo del
        contenido hay DOS barras —la de cobro con el total y el boton Cobrar, y
        la de navegacion— y entre las dos pasan de 130 px, asi que el aviso caia
        justo encima del boton de cobrar y se quedaba ahi hasta seis segundos si
        era un error. En un iPhone hay que sumarle la franja del gesto; en un
        Android sin ella, restarla. Cualquier cambio de altura en esas barras
        vuelve a desalinear el numero, y es la clase de calculo a mano que este
        proyecto no admite en el marco.

        Arriba no hay nada que estorbar: el encabezado del telefono lleva la
        marca y el selector de sucursal, nada que se toque a media venta. Ademas
        es donde el sistema pone sus propios avisos, asi que se lee como un aviso
        del aparato y no como una tarjeta perdida en la pantalla. El relleno sale
        de `pt-safe`, o sea de la muesca real del aparato, no de una constante.

        En escritorio se quedan abajo a la derecha, que es su sitio de siempre y
        ahi no compiten con ningun boton.
      */}
      <div
        className={cx(
          'fixed z-[60] inset-x-0 top-0 pt-safe-2 px-4 flex flex-col items-center gap-2',
          'lg:inset-x-auto lg:right-6 lg:top-auto lg:bottom-6 lg:px-0 lg:pt-0 lg:items-end',
          'pointer-events-none',
        )}
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }): ReactNode {
  const config = {
    success: { icon: <CheckCircle2 className="size-5" />, cls: 'border-primary/30 text-primary' },
    error: { icon: <AlertTriangle className="size-5" />, cls: 'border-danger/30 text-danger' },
    info: { icon: <Info className="size-5" />, cls: 'border-line text-ink' },
  }[toast.tone];

  return (
    <div
      className={cx(
        'pointer-events-auto w-full max-w-md lg:w-auto lg:min-w-72',
        'flex items-start gap-3 rounded-xl border bg-surface shadow-float px-4 py-3',
        // Entra en la direccion en la que esta anclado: cayendo del borde
        // superior en el telefono, subiendo desde la esquina en escritorio.
        'animate-in-down lg:animate-in-up',
        config.cls,
      )}
    >
      <span className="shrink-0 mt-0.5">{config.icon}</span>
      <p className="text-[14px] text-ink flex-1 text-pretty">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar aviso"
        className="shrink-0 -mr-1 -mt-0.5 p-1 rounded-lg text-muted hover:text-ink"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast debe usarse dentro de ToastProvider');
  return value;
}
